<template>
  <v-form>
    <v-data-table
      :headers="headers"
      disable-filtering
      disable-pagination
      disable-sort
      hide-default-footer
      no-data-text=""
    >
      <template v-slot:body.prepend>
        <tr>
          <td>
            <v-autocomplete
              v-model="currFilters.order_number"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="order_number"
              :items="order_numbers"
              :loading="loading"
              item-text="name"
              item-value="name"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
            ></v-autocomplete>
          </td>
          <td>
            <v-autocomplete
              v-model="currFilters.employee"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="employee"
              :items="employees"
              :loading="loading"
              item-text="name"
              item-value="name"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
            ></v-autocomplete>
          </td>
          <td>
            <v-select
              v-model="currFilters.service"
              :items="services"
              item-text="title"
              item-value="id"
              :label="`${$t('ORDERS.SERVICE_NAME')} `"
              :loading="loading"
              chips
              small-chips
              multiple
              clearable
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="remove(data.item, currFilters.service)"
                >
                  {{ data.item.title }}
                </v-chip>
              </template>
            </v-select>
          </td>
          <td>
            <v-autocomplete
              v-model="currFilters.building_complex"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="building_complex"
              :items="buildingComplexes"
              :loading="loading"
              item-text="name"
              item-value="id"
              multiple
              chips
              small-chips
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
              @change="
                () => {
                  building_complex = '';
                }
              "
              ><template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="remove(data.item, currFilters.building_complex)"
                >
                  {{ data.item.name }}
                </v-chip>
              </template></v-autocomplete
            >
          </td>
          <td>
            <v-text-field
              v-model="currFilters.city"
              :label="$t('DATA_TABLE.FILTER')"
              clearable
            ></v-text-field>
          </td>
          <td>
            <v-autocomplete
              v-model="currFilters.customer"
              :label="$t('DATA_TABLE.FILTER')"
              :search-input.sync="customer"
              :items="customers"
              :loading="loading"
              item-text="name"
              item-value="name"
              :no-data-text="$t('TASKS.NO_DATA_FILTER_TEXT')"
              clearable
            ></v-autocomplete>
          </td>
          <td>
            <v-menu
              ref="menuDateFrom"
              v-model="menuDateFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateFrom | dateFormat"
                  :label="`${$t('SUBSCRIPTIONS.DATE_FROM')} `"
                  append-icon="mdi-calendar"
                  clearable
                  readonly
                  role="button"
                  autocomplete="nope"
                  @click:clear="clear_from"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                ref="picker"
                v-model="dateFrom"
                min="1940-01-01"
                first-day-of-week="1"
                @change="saveDatefrom"
              ></v-date-picker>
            </v-menu>

            <v-menu
              ref="menuDateTo"
              v-model="menuDateTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateTo | dateFormat"
                  :label="`${$t('SUBSCRIPTIONS.DATE_TO')}`"
                  append-icon="mdi-calendar"
                  clearable
                  readonly
                  role="button"
                  autocomplete="nope"
                  @click:clear="clear_to"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                ref="picker"
                v-model="dateTo"
                :min="dateFrom || '1940-01-01'"
                first-day-of-week="1"
                @change="saveDateto"
              ></v-date-picker>
            </v-menu>
          </td>
          <td>
            <v-select
              v-model="currFilters.status"
              :items="taskStatuses"
              item-text="name"
              item-value="id"
              :label="$t('DATA_TABLE.FILTER')"
              :loading="loading"
              chips
              small-chips
              multiple
              clearable
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="remove(data.item, currFilters.status)"
                >
                  {{ data.item.name }}
                </v-chip>
              </template>
            </v-select>
          </td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" depressed outlined @click="reset" v-on="on">
                  <v-icon>mdi-filter-remove</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('BUTTON.CLEAR_FILTER') }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
          </td>
          <td>
            <v-btn
              color="primary"
              type="submit"
              depressed
              @click.prevent="setFilters"
            >
              {{ $t('BUTTON.FIND') }}
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-form>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import { debounce, cloneDeep, isEmpty } from 'lodash';
import { mapCacheActions } from 'vuex-cache';
import listFilters from '@/modules/_global/mixins/list-filters';

export default {
  name: 'TasksListFilter',

  filters: {
    dateFormat
  },

  mixins: [listFilters],

  data: () => ({
    unwatch: null,
    options: {},
    delay: 0,

    employee: null,
    order_number: null,
    customer: null,
    employees: [],
    order_numbers: [],
    customers: [],
    services: [],
    service: [],
    building_complex: null,

    menuDateFrom: false,
    dateFrom: null,
    menuDateTo: false,
    dateTo: null,

    expensiveFunc: null
  }),

  computed: {
    ...mapState(['loading']),
    ...mapState('task', ['filters']),
    ...mapState('planning', ['taskStatuses']),
    ...mapState('building_complex', ['buildingComplexes']),
    debouncedRequest() {
      return debounce(this.expensiveFunc, this.delay);
    },
    headers() {
      return [
        {
          text: this.$t('TASKS.ORDER_ID'),
          sortable: false
        },
        {
          text: this.$t('EMPLOYEES.ITEM'),
          sortable: false
        },
        {
          text: this.$t('TASKS.SERVICE'),
          sortable: false
        },
        {
          text: this.$t('BUILDING_COMPLEXES.ITEM'),
          sortable: false
        },
        {
          text: this.$t('PROFILE.CITY'),
          sortable: false
        },
        {
          text: this.$t('ORDERS.CUSTOMER'),
          sortable: false
        },
        {
          text: this.$t('ITEM.DATE'),
          sortable: false
        },
        {
          text: this.$t('EMPLOYEES.STATUS'),
          sortable: false
        },
        {
          text: '',
          sortable: false
        },
        {
          text: '',
          sortable: false
        }
      ];
    }
  },

  watch: {
    dateTo(val) {
      if (val) {
        this.currFilters = { ...this.currFilters, date_to: val };
      } else {
        this.currFilters.date_to = null;
      }
    },

    dateFrom(val) {
      if (val) {
        this.currFilters = { ...this.currFilters, date_from: val };
      } else {
        this.currFilters.date_from = null;
      }
    },

    employee(val) {
      if (val !== this.currFilters.employee && !isEmpty(val)) {
        this.expensiveFunc = this.getDataEmployees;
        this.debouncedRequest();
      }
    },

    order_number(val) {
      if (val !== this.currFilters.order_number && !isEmpty(val)) {
        this.expensiveFunc = this.getDataOrderNumber;
        this.debouncedRequest();
      }
    },

    customer(val) {
      if (val !== this.currFilters.customer && !isEmpty(val)) {
        this.expensiveFunc = this.getDataCustomer;
        this.debouncedRequest();
      }
    }
  },

  mounted() {
    if (!isEmpty(this.filters)) {
      this.currFilters = cloneDeep(this.filters);
      this.customer = this.filters.customer;
      this.getDataCustomer();
      this.employee = this.currFilters.employee;
      this.getDataEmployees();
      this.order_number = this.currFilters.order_number;
      this.getDataOrderNumber();
      this.dateFrom = this.currFilters.date_from;
      this.dateTo = this.currFilters.date_to;
    }

    this.setFilters();
    this.getTaskStatuses();
    this.getDataBuildingComplexes();
    this.retrieveEmployeeServices().then(({ data }) => {
      this.services = data.data;
    });
    this.unwatch = this.$store.watch(
      state => state.localization.lang,
      () => {
        const lang = this.$store.state.localization.lang;

        if (lang) {
          this.retrieveEmployeeServices().then(({ data }) => {
            this.services = data.data;
          });
          this.setFilters();
          this.getTaskStatuses();
        }
      }
    );
    this.setDelay(500);
  },

  destroyed() {
    this.unwatch();
  },

  methods: {
    clear_from() {
      this.dateFrom = null;
    },
    clear_to() {
      this.dateTo = null;
    },

    saveDatefrom(date) {
      this.$refs.menuDateFrom.save(date);
    },

    saveDateto(date) {
      this.$refs.menuDateTo.save(date);
    },

    expensiveOperation: function() {
      return this.debouncedRequest();
    },

    setDelay(v) {
      this.delay = v;
    },
    ...mapActions('task', [
      'filterOrderNumber',
      'filterEmployee',
      'filterCustomer'
    ]),
    ...mapActions('service', ['retrieveEmployeeServices']),
    ...mapActions('planning', ['getTaskStatuses']),
    getDataEmployees() {
      this.filterEmployee({ search: this.employee })
        .then(({ data }) => {
          this.employees = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    getDataOrderNumber() {
      this.filterOrderNumber({ search: this.order_number })
        .then(({ data }) => {
          this.order_numbers = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    getDataCustomer() {
      this.filterCustomer({ search: this.customer })
        .then(({ data }) => {
          this.customers = data.data;
        })
        .catch(error => {
          this.error = error;
        });
    },
    ...mapCacheActions('building_complex', ['getBuildingComplexes']),
    getDataBuildingComplexes() {
      this.getBuildingComplexes().catch(error => {
        this.error = error;
      });
    },
    remove(item, obj) {
      const index = obj.indexOf(item.id);
      if (index >= 0) obj.splice(index, 1);
    },
    reset() {
      this.employee = null;
      this.order_number = null;
      this.customer = null;
      this.building_complex = null;
      this.currFilters = {};
      this.menuDateFrom = false;
      this.dateFrom = null;
      this.menuDateTo = false;
      this.dateTo = null;
      this.expensiveFunc = null;

      this.setFilters();
    }
  }
};
</script>
